import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  API_URLS,
  fetcher,
  revalidateCourse,
  useAbandonAttempt,
  useTestFinish,
  useTestModel,
} from "@repo/edtech-api";
import { normalizeUrl } from "@repo/edtech-shared";
import { Loader } from "@repo/edtech-ui-kit";
import clsx from "clsx";
import { mutate } from "swr";

import { isMaterialSurvey } from "../../../../lib";
import {
  DEFAULT_BASE_URL,
  ExtendedLesson,
  LessonStatus,
  TEST_SHOW_RESULT_FLAG,
  TestData,
  TestSolutionProvider,
  useIdContext,
  useMainContextActions,
  useMainContextValues,
  useTestSolutionActionsContext,
  useTestSolutionValuesContext,
} from "../../../../model";
import { formatTestResult } from "../../course-content/lib/helpers";
import {
  ExpiredTestCover,
  MainTestCover,
  TestSolution,
} from "./components";

import st from "./Test.module.scss";

type Props = {
  lesson: ExtendedLesson & { payload: Record<string, string> | string },
  className?: string,
};

type InnerProps = Props;

const TestContent: FC<InnerProps> = ({
  lesson,
  className,
}) => {
  const { nextLessonAction, updateLibraryAction } = useMainContextActions();
  const {
    isLastLesson,
    isNextLessonAvailable,
    currentLesson,
    isCoursePassed,
    hasSuccessScreen,
  } = useMainContextValues();

  const {
    startTest,
    continueTest,
    restartTest,
    timerSynced,
    resumeAfterTimeout,
    finishTestSucceeded,
    finishTestExpired,
  } = useTestSolutionActionsContext();

  const {
    isTestInProgress,
    isTestExpired,
    isTestFinished,
    currentQuestionIndex,
  } = useTestSolutionValuesContext();

  const isSurvey = isMaterialSurvey(lesson.survey);
  const testStatus = lesson?.status;
  const testResult = formatTestResult(lesson.payload);

  const { publicationId, unitId: mainUnitId } = useIdContext();
  const { trigger } = useAbandonAttempt();
  const isLessonInProgress = testStatus === LessonStatus.InProgress;
  const [ prefetchedTestData, setPrefetchedTestData ] = useState<TestData | undefined>(undefined);
  const [ isRetakeLoading, setIsRetakeLoading ] = useState(false);
  const [ isExpiredFinishLoading, setIsExpiredFinishLoading ] = useState(false);

  const isNeedLoadTest = useMemo(() => (
    (isLessonInProgress || isTestInProgress) &&
    !isTestFinished
  ), [
    isLessonInProgress,
    isTestInProgress,
    isTestFinished,
  ]);

  const { unitParentId: unitId } = currentLesson ?? {};

  const {
    isLoading,
    data: testData,
  } = useTestModel({
    publicationid: publicationId,
    unitid: unitId ?? "",
    testid: lesson?.uid,
    enabled: isNeedLoadTest && !prefetchedTestData,
  });
  const resolvedTestData = prefetchedTestData ?? testData as TestData | undefined;
  const isTestDataLoading = !prefetchedTestData && isLoading;

  const normalizedQuestions = useMemo(() => (
    resolvedTestData?.questions?.map(({
      question,
      uid,
    }) => ({
      title: question,
      uid,
    })) || []
  ), [ resolvedTestData?.questions ]);

  const { trigger: finishExpiredTest } = useTestFinish({
    publicationId,
    unitId: unitId ?? "",
    testId: resolvedTestData?.uid,
    questionId: normalizedQuestions[ currentQuestionIndex ]?.uid,
    session: resolvedTestData?.session,
    mainUnitId,
    timeout: resolvedTestData?.testTimeout,
    skillLevel: resolvedTestData?.skillLevel,
    survey: isSurvey,
  });

  const onClickFinishExpiredTest = useCallback(async () => {
    if (isExpiredFinishLoading) return;

    setIsExpiredFinishLoading(true);
    try {
      updateLibraryAction(currentLesson?.nodeId || "", {
        status: LessonStatus.Queued,
      });

      const res = await finishExpiredTest();

      if (res?.expired) {
        finishTestExpired();
        return;
      }

      finishTestSucceeded();
    } catch (error) {
      console.error("Failed to finish expired test:", error);
    } finally {
      setIsExpiredFinishLoading(false);
    }
  }, [
    currentLesson?.nodeId,
    finishExpiredTest,
    finishTestExpired,
    finishTestSucceeded,
    isExpiredFinishLoading,
    updateLibraryAction,
  ]);

  useEffect(() => {
    if (!resolvedTestData?.uid) return;

    timerSynced({
      remainingTime: resolvedTestData?.remainingTime ?? null,
      hasTestTimeout: Boolean(resolvedTestData?.testTimeout),
      receivedAt: Date.now(),
    });
  }, [
    resolvedTestData?.remainingTime,
    resolvedTestData?.session,
    resolvedTestData?.testTimeout,
    resolvedTestData?.uid,
    timerSynced,
  ]);

  useEffect(() => {
    if (isTestInProgress) return;

    setPrefetchedTestData(undefined);
  }, [ isTestInProgress ]);

  const onClickStartTest = () => {
    startTest();
  };

  const onClickNewTest = useCallback(async () => {
    if (isRetakeLoading) return;

    setIsRetakeLoading(true);
    try {
      const testModelKey = [
        API_URLS.TEST_MODEL,
        publicationId,
        unitId,
        lesson?.uid,
      ] as const;
      const loadFreshTestData = async () => {
        const freshData = await fetcher<TestData>({
          url: API_URLS.TEST_MODEL,
          params: {
            publicationid: publicationId,
            unitid: unitId ?? "",
            testid: lesson?.uid ?? "",
            hidejson: false,
          },
        });

        await mutate(
          testModelKey,
          freshData,
          false,
        );

        return freshData;
      };

      if (isLessonInProgress && resolvedTestData?.session) {
        await trigger({
          session: resolvedTestData.session,
        });
        await revalidateCourse(publicationId, mainUnitId);
        const freshData = await loadFreshTestData();
        setPrefetchedTestData(freshData);

        restartTest();
        return;
      }

      await revalidateCourse(publicationId, mainUnitId);
      const freshData = await loadFreshTestData();
      setPrefetchedTestData(freshData);
      restartTest();
    } catch (error) {
      console.error("Failed to abandon test attempt:", error);
    } finally {
      setIsRetakeLoading(false);
    }
  }, [
    isRetakeLoading,
    lesson?.uid,
    publicationId,
    mainUnitId,
    restartTest,
    isLessonInProgress,
    resolvedTestData?.session,
    trigger,
    unitId,
  ]);

  const onClickContinueTest = useCallback(() => {
    continueTest(resolvedTestData?.questions || []);
  }, [ continueTest, resolvedTestData?.questions ]);

  const onClickFinishCourse = () => {
    const certificatePath = (window as Window & { certificatePath?: string }).certificatePath;

    window.open(
      normalizeUrl(certificatePath ?
        certificatePath :
        DEFAULT_BASE_URL,
      `success_screen_render?publicationid=${publicationId}`),
      "_blank",
      "noopener,noreferrer",
    );
  };

  if (isTestDataLoading) {
    return (
      <div className={clsx(st.wrap, className)}>
        <Loader />
      </div>
    );
  }

  if (isTestInProgress && isTestExpired) {
    return (
      <div className={clsx(st.wrap, className)}>
        <ExpiredTestCover
          isFinishLoading={isExpiredFinishLoading}
          onClickContinue={resumeAfterTimeout}
          onClickFinishTest={onClickFinishExpiredTest}
        />
      </div>
    );
  }

  if (isTestInProgress) {
    return (
      <div className={clsx(st.wrap, className)}>
        <TestSolution
          isSurvey={isSurvey}
          questions={normalizedQuestions}
          testData={resolvedTestData}
        />
      </div>
    );
  }

  return (
    <div className={clsx(st.wrap, className)}>
      <MainTestCover
        hasSuccessScreen={hasSuccessScreen}
        isCoursePassed={isCoursePassed}
        isLastLesson={isLastLesson}
        isNextLessonDisabled={!isNextLessonAvailable}
        isRetakeLoading={isRetakeLoading}
        isShowReport={lesson?.showReport === TEST_SHOW_RESULT_FLAG}
        isSurvey={isSurvey}
        isTestExpired={isTestExpired}
        lesson={lesson}
        testResult={testResult}
        testStatus={testStatus!}
        onClickContinueTest={onClickContinueTest}
        onClickFinishCourse={onClickFinishCourse}
        onClickNewTest={onClickNewTest}
        onClickNextLesson={nextLessonAction}
        onClickStartTest={onClickStartTest}
      />
    </div>
  );
};

export const Test: FC<Props> = (props) => {
  return (
    <TestSolutionProvider>
      <TestContent {...props} />
    </TestSolutionProvider>
  );
};
